import { UniIconButton } from '@/design-system/components/buttons';
import { TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, SafeAreaView, ScrollView, Pressable, ActivityIndicator } from 'dripsy';
import { uniqBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput } from '@/design-system/components/inputs';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { DigitalDepositStackParamList, DigitalDepositStackRoutes } from '@/features/account-management/screens/digital-deposit/type';
import { PageHeaderModalNative } from '@/features/account-management/screens/page/PageHeaderModalNative';
import { useDigitalAssets } from '@/features/account-management/screens/details/hooks/useAccount.hook';
import { CurrenciesIcon } from '@/features/earn/components';
import { Blockchain, DigitalAsset } from '@utx/utx-access-client';

export interface DigitalDepositProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  account: string;
  record?: any;
  refresh?: () => void;
}

export const DigitalDepositNativeListScreen: React.FC<DigitalDepositProps> = (props) => {
  const { onClose, onLayout, account, record, refresh } = props;
  const { t } = useTranslation('digital-injection');
  const { container, body, textMb } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<DigitalDepositStackParamList>>();
  const { theme } = useDripsyTheme();

  const [search, setSearch] = useState('');
  const { isLoadingDigitalAssets, assets, chains } = useDigitalAssets({});
  const [assetsDispay, setAssetsDispay] = useState<(DigitalAsset & { assetChains: string[] })[]>([]);
  useEffect(() => {
    if (isLoadingDigitalAssets) return;
    let assetsFlag: (DigitalAsset & { assetChains: string[] })[] = [];
    assets.forEach((asset) => {
      let index = assetsFlag.findIndex((af) => af.name == asset.name);
      if (index == -1) {
        assetsFlag.push({ ...asset, assetChains: [asset.chain || ''] });
      } else {
        assetsFlag[index].assetChains.push(asset.chain || '');
      }
    });

    if (!search) setAssetsDispay(assetsFlag);
    let assetsDispayFlag = assetsFlag?.filter((asset: DigitalAsset & { assetChains: string[] }) => {
      return (
        asset?.chain && record?.assets?.includes(asset?.name) && asset?.name?.toLocaleLowerCase()?.includes(search?.toLocaleLowerCase())
      );
    });
    setAssetsDispay(assetsDispayFlag);
  }, [search, isLoadingDigitalAssets]);

  return (
    <SafeAreaView sx={container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View sx={{ height: '100%', backgroundColor: '$white' }}>
          <PageHeaderModalNative>
            <View sx={{ px: '$4', mt: '$5', mb: '$4' }}>
              <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
                <UniIconButton
                  icon="arrowLeft"
                  options={{ iconStyles: { color: theme?.colors?.$white as string } }}
                  onPress={onClose}></UniIconButton>
              </View>
              <View sx={{ textAlign: 'center' }}>
                <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
                  {t('digital-injection::glossary.digital-assets')}
                </TextTitle>
              </View>
            </View>
          </PageHeaderModalNative>
          <View sx={body}>
            <TextTitle sx={textMb} semiBold>
              {t('digital-injection::select-asset.text--please-select-asset')}
            </TextTitle>
            <View sx={{ pb: '$3' }}>
              <UniInput
                prefixIcon="search"
                wrapperSx={{ flex: 1 }}
                sx={{ width: '100%' }}
                onValueChange={(value) => setSearch(value || '')}
                placeholder={t('digital-injection::glossary.search-by-asset')}
              />
            </View>
            <View sx={{ flexDirection: 'row', flexWrap: 'wrap' }}>
              {!isLoadingDigitalAssets ? (
                assetsDispay.length > 0 ? (
                  assetsDispay?.map((asset, index) => (
                    <View
                      sx={{
                        width: '48%',
                        my: '$1',
                        mr: index % 2 == 0 ? '4%' : undefined,
                        padding: '$3',
                        border: '1px solid #fafafa',
                        backgroundColor: '#fafafa',
                        borderRadius: '$radius1'
                      }}>
                      <Pressable
                        onPress={() => {
                          navigation.navigate(DigitalDepositStackRoutes.Address, {
                            onLayout,
                            account,
                            refresh,
                            record: { ...asset, chains: chains }
                          });
                        }}>
                        <CurrenciesIcon
                          asset={asset?.name || ''}
                          textSize="textTitle"
                          textSemiBold
                          textSx={{ color: '$textPrimary' }}
                          textBold={false}
                        />
                      </Pressable>
                    </View>
                  ))
                ) : (
                  <View sx={{ width: '100%', display: 'flex', alignItems: 'center', paddingTop: 20 }}>
                    {t('digital-injection::injection-instruction.text--no-digital-asset-available')}
                  </View>
                )
              ) : (
                <View sx={{ padding: '$5' }}>
                  <ActivityIndicator size="large" color={(theme.colors?.$disable as string) || '#F0F0F0'} />
                </View>
              )}
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '$backgroundPrimary',
    zIndex: -100
  },
  body: {
    px: 16,
    py: 20
  },
  textMb: {
    marginBottom: 20
  }
};
